<template>
  <div class="wc-card-wrapper">
    <div class="card wc-card" :class="platformModifier">
      <router-link
        :to="`/i/${item.id}`"
        class="wc-product-card-link"
        aria-hidden="true"
        tabindex="-1"
      ></router-link>
      <div class="wc-card__upper">
        <div class="wc-header-image-badge-wrapper">
          <WCProductCardBadgesModern :product="item" />
        </div>
        <div class="product-card-header">
          <WCFavoriteButton
            class="wc-product-card__favorite-btn"
            v-if="isCustomer"
            :item="item"
            :isMobile="false"
          />
        </div>
        <div class="wc-header-image-wrapper">
          <router-link :to="`/i/${item.id}`" class="d-block w-100">
            <WCImage
              :imageSrc="getProductImage(item.image)"
              :imageAlt="productImgAltText(item.name, item.brand)"
              imageClass="wc-header-image"
              defaultClass="d-block w-100 wc-header-image"
              :defaultTitle="noProductImageAltText(item.name)"
            />
          </router-link>
        </div>

        <div class="wc-card-body">
          <div class="mb-2">
            <div class="d-flex flex-column flex-grow-1 wc-card_content">
              <div v-if="item.brand" class="wc-product-card__brand">
                <router-link :to="`/b?g=BRD-${item.brandId}`">
                  {{ item.brand }}
                </router-link>
              </div>
              <router-link :to="`/i/${item.id}`" class="wc-product-card__nameLink">
                <div class="wc-product-card__name">{{ item.name }}</div>
                <div v-if="item.size" class="wc-product-card__size">{{ item.size }}</div>
              </router-link>
            </div>

            <div class="d-flex justify-content-between flex-grow-1 wc-product-card__price-row">
              <WCProductPriceCardModern
                class="flex-fill font-size-sm"
                :item="item"
                v-if="!shouldHideItemPrices"
              />
            </div>
          </div>
          <div class="wc-product-card__bottom-wrapper">
            <div class="d-flex flex-column justify-content-between">
              <WCPaymentOptionBadgesModernCard :item="props.item" :ebtOnly="true" />
            </div>
          </div>
        </div>
      </div>

      <div class="wc-product-card__atc-zone wc-card-hover">
        <WCAddToCartModernCardButton v-if="!isScanMode" :item="item">
          <WCAddToCartQuantityAdjusterModernCard :item="item" />
        </WCAddToCartModernCardButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineProps } from 'vue';
import { useStore } from 'vuex';
import WCFavoriteButton from '@/modules/lists/components/WCFavoriteButton/WCFavoriteButton.vue';
import WCProductPriceCardModern from '@/components/WCProductPriceCardModern/WCProductPriceCardModern.vue';
import WCImage from '@/components/WCImage/WCImage.vue';
import useHidePricing from '@/composables/useHidePricing';
import useProductImageInfo from '@/views/ProductDetailPage/composables/useProductImageInfo';
import ItemDetail from '@/models/entities/items/item-detail';
import WCPaymentOptionBadgesModernCard from '@/components/WCPaymentOptionBadgesModernCard/WCPaymentOptionBadgesModernCard.vue';
import WCAddToCartModernCardButton from '@/modules/cart/components/WCAddToCartModernCardButton/WCAddToCartModernCardButton.vue';
import WCAddToCartQuantityAdjusterModernCard from '@/modules/cart/components/WCAddToCartQuantityAdjusterModernCard/WCAddToCartQuantityAdjusterModernCard.vue';
import usePlatform from '@/modules/platform/composables/usePlatform';
import WCProductCardBadgesModern from '../WCProductCardBadgesModern/WCProductCardBadgesModern.vue';

interface Props {
  item: ItemDetail;
}

const props = defineProps<Props>();

const store = useStore();
const isCustomer = computed(() => store.getters['user/isCustomer']);
const { shouldHideItemPrices } = useHidePricing();
const { getProductImage, productImgAltText, noProductImageAltText } = useProductImageInfo();
const isScanMode = computed(() => store.getters['user/isCustomerModeScan']);
const { platform } = usePlatform();
const platformModifier = computed(() =>
  platform.value.isMobile ? 'wc-card--mobile' : 'wc-card--desktop',
);
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-card {
  border: none;
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--white);
  box-shadow: 0 0.125rem 0.5rem rgba(0, 0, 0, 0.1);
}

.wc-card--mobile {
  border-radius: 0.75rem;
}

.wc-card--desktop {
  position: relative;
  border-radius: 0.5rem;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: convert-px-to-rem(60) auto auto 1fr auto auto;
}

.wc-card__upper {
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;
  overflow: hidden;
}

.wc-card--mobile .wc-card__upper {
  border-radius: 0.75rem 0.75rem 0 0;
}

.wc-card--desktop .wc-card__upper {
  display: contents;
}

.wc-card-body {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  padding: 0.375rem 0.5rem 0;
}

.wc-card--desktop .wc-card-body {
  display: contents;
}

.wc-card-body:not(:has(.wc-payment-badges)) .mb-2 {
  margin-bottom: 1rem;
}

.wc-card-content {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;

  &.wc-middle-content {
    align-items: center;
  }

  .wc-card-select {
    width: 5rem;
    margin-top: $margin-2;
  }

  .wc-card-button {
    height: 2.375rem;
    min-width: 7.5rem;
    white-space: nowrap;
    margin-top: $margin-2;
  }

  .wc-list-button {
    padding-right: 0;
  }
}

.wc-card_content {
  margin-bottom: 0.125rem;
  padding-top: 0.375rem;
}

.mb-2 {
  display: flex;
  flex-direction: column;
}

.wc-product-card__price-row {
  order: -1;
}

.wc-product-card__brand {
  font-size: convert-px-to-rem(11);
  line-height: convert-px-to-rem(13);
  font-weight: 400;
  color: var(--dark, $dark);
  margin-bottom: 0.125rem;

  a {
    color: inherit;
    text-decoration: underline;
    cursor: pointer;
  }
}

.wc-product-card__name {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  font-size: $font-size-sm;
  line-height: convert-px-to-rem(17);
  margin-bottom: convert-px-to-rem(1);
  color: var(--dark, $dark);
  font-weight: 700;
  width: 100%;
  text-decoration: none;
}

.wc-product-card__size {
  font-size: $font-size-xs;
  line-height: convert-px-to-rem(15);
  margin-bottom: convert-px-to-rem(6);
  font-weight: 400;
  color: var(--dark, $dark);
}

.wc-product-card__nameLink {
  text-decoration: none;
}

.wc-card--desktop .wc-product-card__brand {
  font-size: convert-px-to-rem(13);
  line-height: convert-px-to-rem(16);
  margin-bottom: convert-px-to-rem(4);
}

.wc-card--desktop .wc-product-card__name {
  font-size: $font-size-base;
  line-height: convert-px-to-rem(20);
  font-weight: 500;
}

.wc-card--desktop .wc-product-card__size {
  font-size: $font-size-sm;
  line-height: convert-px-to-rem(18);
}

.wc-card-wrapper {
  display: flex;
  flex-direction: row;
}

.wc-product-card__favorite-btn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  padding: 0;
  pointer-events: auto;
}

.wc-header-image-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 7.5rem;
  margin: 0.75rem 0.3125rem 0;
  padding: 0;

  > a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }
}

.product-card-header {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  pointer-events: none;
}

.wc-header-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  flex-shrink: 0;
}

.wc-product-card__bottom-wrapper {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
  z-index: 2;
}

.wc-product-card-link {
  position: absolute;
  inset: 0;
  z-index: 1;
}

.wc-product-card__atc-zone {
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 3;
}

.wc-product-card__atc-zone :deep(.wc-atc-qty-adjuster-modern-card__atc),
.wc-product-card__atc-zone :deep(.wc-atc-qty-adjuster-modern-card),
.wc-product-card__atc-zone :deep(button.wc-atc-button-modern__action),
.wc-product-card__atc-zone :deep(.wc-atc-button-modern__pill),
.wc-product-card__atc-zone :deep(.wc-atc-button-modern__multi-ot) {
  margin: 0 convert-px-to-rem(10) convert-px-to-rem(16) convert-px-to-rem(14);
  width: calc(100% - #{convert-px-to-rem(24)});
}

.wc-product-card__atc-zone :deep(.wc-atc-qty-adjuster-modern-card__atc),
.wc-product-card__atc-zone :deep(.wc-atc-qty-adjuster-modern-card),
.wc-product-card__atc-zone :deep(button.wc-atc-button-modern__action),
.wc-product-card__atc-zone :deep(.wc-atc-button-modern__multi-ot) {
  border: convert-px-to-rem(1.5) solid var(--primary, $primary);
  border-radius: 999px;
}

.wc-product-card__atc-zone :deep(.wc-atc-button-modern__multi-ot) {
  overflow: hidden;
}

.wc-product-card__atc-zone
  :deep(.wc-atc-button-modern__multi-ot-add .wc-atc-qty-adjuster-modern-card__atc),
.wc-product-card__atc-zone
  :deep(.wc-atc-button-modern__multi-ot-add .wc-atc-qty-adjuster-modern-card),
.wc-product-card__atc-zone
  :deep(.wc-atc-button-modern__multi-ot-add button.wc-atc-button-modern__action) {
  margin: 0;
  width: 100%;
  border: none;
  border-radius: 0;
}

.wc-product-card__atc-zone :deep(.wc-atc-button-modern__multi-ot-caret) {
  border-top: none;
  border-left: convert-px-to-rem(1) solid var(--primary-lighter-7);
  border-radius: 0;
}

.wc-product-card__atc-zone
  :deep(.wc-atc-button-modern__multi-ot:has(.wc-atc-qty-adjuster-modern-card)
    .wc-atc-button-modern__multi-ot-caret) {
  border-right: none;
  border-bottom: none;
  border-radius: 0;
}

.wc-product-card__atc-zone :deep(.wc-atc-button-modern__pill) {
  background-color: var(--white);
  border: convert-px-to-rem(1.5) solid var(--primary, $primary);
  border-radius: 999px;
  color: var(--primary, $primary);
  opacity: 0.45;
  cursor: not-allowed;
}

.wc-header-image-badge-wrapper {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
}

.wc-card--desktop .wc-header-image-badge-wrapper {
  position: static;
  grid-row: 1;
  align-self: start;
  height: convert-px-to-rem(60);
  box-sizing: border-box;
  padding: convert-px-to-rem(8) convert-px-to-rem(8) convert-px-to-rem(4);
  gap: convert-px-to-rem(3);
  overflow: hidden;
}

.wc-card--desktop .wc-header-image-wrapper {
  grid-row: 2;
  height: convert-px-to-rem(132);
  margin-top: 0;
}

.wc-card--desktop .mb-2 {
  grid-row: 3;
  padding: convert-px-to-rem(20) convert-px-to-rem(16) convert-px-to-rem(20);
}

.wc-card--desktop .wc-product-card__bottom-wrapper {
  grid-row: 4;
  align-self: end;
  margin-top: 0;
}

.wc-card--desktop :deep(.wc-payment-badges) {
  margin-top: convert-px-to-rem(4);
  margin-bottom: convert-px-to-rem(10);
  padding-left: convert-px-to-rem(16);
  padding-right: convert-px-to-rem(16);
}

.wc-card--desktop :deep(.wc-payment-badge) {
  font-size: convert-px-to-rem(11);
  padding: convert-px-to-rem(3) convert-px-to-rem(7);
}

.wc-card--desktop :deep(.wc-badge) {
  font-size: $font-size-xs;
  padding: convert-px-to-rem(4) convert-px-to-rem(9);
}

.wc-card--desktop :deep(.wc-badge__dot) {
  width: convert-px-to-rem(7);
  height: convert-px-to-rem(7);
}

.wc-card--desktop .wc-product-card__atc-zone {
  grid-row: 5;
  align-self: end;
}
</style>
